import React, { useEffect, useState } from 'react';
import { IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import Overlay from '../Overlay';
import { remainingCreditable, remainingCreditableForLine } from '../../lib/vendorCredit';

// Issue #299, Session 74z5. Records a vendor credit against one invoice.
//
// A NEW COMPONENT RATHER THAN A MODE ON EnterInvoiceSheet. The mockup gate settled
// that the entry path is deliberately not the Enter Invoice sheet, and the code
// agrees with the design: this writes no quantity, runs no receipt check, has its own
// RPC and its own ceilings. Locked rule 14 is about a duplicated mutation sequence,
// and there isn't one here. Same call ResolveDiscrepancySheet made at #301.
//
// THE INVOICE BEING CREDITED IS FIXED, NOT SELECTABLE. The sheet is opened from that
// invoice's own row, which is what makes credit_of_invoice_id structural rather than
// a field someone can fill in wrong — there is no orphan-credit path to validate
// because there is no way to express one.
//
// Rendered through Overlay's portal, never position: fixed (locked rule 3).
export default function RecordCreditSheet({
  visible, invoice, req, docs, lines, linesLoading, isMobile,
  saving, error, done, result,
  onSubmit, onClose,
}) {
  const [ref, setRef] = useState('');
  const [date, setDate] = useState('');
  const [amounts, setAmounts] = useState({});

  // Reset whenever the sheet opens on a different invoice. Carrying a memo number
  // or an amount across would attach one vendor's document to another's.
  useEffect(() => {
    if (visible) {
      setRef('');
      setDate(new Date().toISOString().slice(0, 10));
      setAmounts({});
    }
  }, [visible, invoice?.id]);

  if (!visible || !invoice) return null;

  const accent = '#B31F7C';
  const billedLines = (lines || []).filter(l => String(l.invoice_id) === String(invoice.id));
  const cap = remainingCreditable(invoice, docs);
  const alreadyCredited = parseFloat(invoice.total_amount || 0) - cap;

  function fmt(n) {
    if (n == null || isNaN(n)) return '—';
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function amountFor(itemId) {
    const raw = amounts[itemId];
    if (raw == null || String(raw).trim() === '') return 0;
    const n = parseFloat(raw);
    return isNaN(n) ? 0 : n;
  }

  const total = billedLines.reduce((sum, l) => sum + amountFor(l.request_item_id), 0);

  // Per-line and invoice-level ceilings are both checked here so the user sees the
  // limit before committing. The database enforces both again in
  // enter_material_request_credit() — this is the courtesy, that is the control.
  const lineErrors = billedLines
    .map(l => {
      const { remaining } = remainingCreditableForLine(invoice, docs, lines, l.request_item_id);
      const entered = amountFor(l.request_item_id);
      if (entered < 0) return 'Credit amounts are entered positive.';
      if (entered > remaining) return 'One line is over what this invoice billed for it.';
      return null;
    })
    .filter(Boolean);

  const overCap = total > cap + 0.0001;
  const trimmedRef = ref.trim();
  const canSubmit = !saving && !done
    && trimmedRef.length > 0
    && !!date
    && total > 0
    && !overCap
    && lineErrors.length === 0;

  const isSettled = req?.status === 'closed';

  function submit() {
    if (!canSubmit) return;
    onSubmit({
      creditRef: trimmedRef,
      creditDate: date,
      items: billedLines
        .map(l => ({ request_item_id: l.request_item_id, credit_amount: amountFor(l.request_item_id) }))
        .filter(i => i.credit_amount > 0),
    });
  }

  const labelStyle = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#7A7A7A', marginBottom: 4 };
  // 16px is not a style choice — anything smaller triggers iOS auto-zoom (locked rule 12).
  const inputStyle = { width: '100%', fontSize: 16, padding: '9px 11px', border: '1px solid #D8D8D8', borderRadius: 8, outline: 'none', boxSizing: 'border-box' };

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={560} onClose={saving ? undefined : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />}

        <div style={{ background: accent, padding: '14px 20px', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Record vendor credit</div>
          <div style={{ fontSize: 11, color: '#F3D3E5', marginTop: 2 }}>
            Against {invoice.invoice_ref || 'no invoice number'}
            {req?.request_number ? ' · ' + req.request_number : ''}
          </div>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '18px 0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: accent, marginBottom: 6 }}>Credit recorded</div>
              <div style={{ fontSize: 13, color: '#5C5C5C' }}>
                {fmt(result?.credit_total)} credited against {invoice.invoice_ref || 'this invoice'}.
              </div>
            </div>
          ) : linesLoading ? (
            <div style={{ fontSize: 13, color: '#7A7A7A', padding: '18px 0', textAlign: 'center' }}>Loading the invoice lines…</div>
          ) : billedLines.length === 0 ? (
            <div style={{ fontSize: 13, color: '#A32D2D', padding: '18px 0', textAlign: 'center' }}>
              This invoice has no recorded lines, so there is nothing to credit against.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px' }}>
                  <div style={labelStyle}>Credit memo number</div>
                  <input
                    style={inputStyle}
                    value={ref}
                    onChange={e => setRef(e.target.value)}
                    placeholder="CM-2210"
                  />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <div style={labelStyle}>Credit date</div>
                  <input type="date" style={inputStyle} value={date} onChange={e => setDate(e.target.value)} />
                </div>
              </div>

              <div style={labelStyle}>Lines on this invoice</div>
              <div style={{ border: '1px solid #E8E8E8', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                {billedLines.map((l, i) => {
                  const { billed, remaining } = remainingCreditableForLine(invoice, docs, lines, l.request_item_id);
                  const entered = amountFor(l.request_item_id);
                  const over = entered > remaining;
                  return (
                    <div key={l.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '9px 11px', borderBottom: i === billedLines.length - 1 ? 'none' : '1px solid #F0F0F0',
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#0F0F0F' }}>{l.label || ('Line ' + l.request_item_id)}</div>
                        <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 1 }}>
                          billed {fmt(billed)}
                          {remaining < billed ? ' · ' + fmt(remaining) + ' still creditable' : ''}
                        </div>
                      </div>
                      <input
                        inputMode="decimal"
                        style={{
                          ...inputStyle, width: 104, flexShrink: 0, textAlign: 'right',
                          border: '1px solid ' + (over ? '#E24B4A' : entered > 0 ? accent : '#D8D8D8'),
                          color: over ? '#A32D2D' : entered > 0 ? accent : '#0F0F0F',
                          fontWeight: entered > 0 ? 700 : 400,
                        }}
                        value={amounts[l.request_item_id] ?? ''}
                        onChange={e => setAmounts(a => ({ ...a, [l.request_item_id]: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                  );
                })}
              </div>

              <div style={{
                background: overCap ? '#FFEBEE' : '#FBEDF5',
                border: '1px solid ' + (overCap ? '#F3C6C6' : '#EFC8DF'),
                borderRadius: 8, padding: '9px 12px', fontSize: 12.5,
                color: overCap ? '#A32D2D' : '#8A2464', marginBottom: 12,
                display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <IconInfoCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  Credit total {fmt(total)} — cannot exceed the {fmt(cap)} still creditable on this invoice
                  {alreadyCredited > 0 ? ' (' + fmt(alreadyCredited) + ' already credited)' : ''}.
                </span>
              </div>

              {isSettled && (
                <div style={{
                  background: '#FFF8EC', border: '1px solid #F3D9A8', borderRadius: 8,
                  padding: '9px 12px', fontSize: 12.5, color: '#8A5A08', marginBottom: 12,
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <IconAlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>This order is settled. The credit changes what it invoiced for; it does not reopen the order, because nothing about what arrived has changed.</span>
                </div>
              )}

              {error && (
                <div style={{ background: '#FFEBEE', border: '1px solid #F3C6C6', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: '#A32D2D', marginBottom: 12 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 9, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{ padding: '9px 15px', background: 'white', color: '#5C5C5C', border: '1px solid #D8D8D8', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}
          >
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              style={{
                padding: '9px 15px', background: canSubmit ? accent : '#E0E0E0',
                color: canSubmit ? 'white' : '#9A9A9A', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'default',
              }}
            >
              {saving ? 'Recording…' : 'Record credit'}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}
